import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { CartProvider } from "@/lib/cart";
import { AuthProvider, useAuth } from "@/lib/auth";

import PublicLayout from "@/components/PublicLayout";
import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";
import About from "@/pages/About";
import CustomerLogin from "@/pages/CustomerLogin";
import CustomerRegister from "@/pages/CustomerRegister";
import CustomerAccount from "@/pages/CustomerAccount";
import CustomerOrderDetail from "@/pages/CustomerOrderDetail";
import LegalPage from "@/pages/LegalPage";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import PrinterLanding from "@/pages/PrinterLanding";
import PrintersIndex from "@/pages/PrintersIndex";
import CustomerWishlist from "@/pages/CustomerWishlist";
import Solucoes from "@/pages/Solucoes";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminShipping from "@/pages/admin/AdminShipping";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminContent from "@/pages/admin/AdminContent";
import AdminBanners from "@/pages/admin/AdminBanners";
import AdminSuppliers from "@/pages/admin/AdminSuppliers";
import AdminCampaigns from "@/pages/admin/AdminCampaigns";
import AdminBrands from "@/pages/admin/AdminBrands";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import Admin2FA from "@/pages/admin/Admin2FA";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminServices from "@/pages/admin/AdminServices";
import AdminPortfolio from "@/pages/admin/AdminPortfolio";
import AdminContactRequests from "@/pages/admin/AdminContactRequests";
import Analytics from "@/components/Analytics";

function ProtectedAdmin({ children }) {
    const { user, loading } = useAuth();
    if (loading)
        return (
            <div className="min-h-screen flex items-center justify-center font-mono">
                A carregar…
            </div>
        );
    if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;
    return children;
}

function ProtectedCustomer({ children }) {
    const { user, loading } = useAuth();
    if (loading)
        return (
            <div className="min-h-screen flex items-center justify-center font-mono">
                A carregar…
            </div>
        );
    if (!user) return <Navigate to="/conta/login" replace />;
    return children;
}

function App() {
    return (
        <div className="App">
            <HelmetProvider>
            <AuthProvider>
                <CartProvider>
                    <BrowserRouter>
                        <Analytics />
                        <Toaster
                            position="top-right"
                            toastOptions={{ className: "border-2 border-ink rounded-none" }}
                        />
                        <Routes>
                            <Route element={<PublicLayout />}>
                                <Route path="/" element={<Home />} />
                                <Route path="/loja" element={<Catalog />} />
                                <Route path="/loja/:categoria" element={<Catalog />} />
                                <Route path="/produto/:id" element={<ProductDetail />} />
                                <Route path="/carrinho" element={<Cart />} />
                                <Route path="/checkout" element={<Checkout />} />
                                <Route
                                    path="/encomenda/:id/sucesso"
                                    element={<OrderSuccess />}
                                />
                                <Route path="/sobre" element={<About />} />
                                <Route path="/servicos" element={<Solucoes />} />
                                <Route path="/impressoras" element={<PrintersIndex />} />
                                <Route path="/impressora/:slug" element={<PrinterLanding />} />
                                <Route path="/legal/:slug" element={<LegalPage />} />
                                <Route path="/conta/login" element={<CustomerLogin />} />
                                <Route path="/conta/registar" element={<CustomerRegister />} />
                                <Route path="/conta/recuperar-senha" element={<ForgotPassword />} />
                                <Route
                                    path="/conta"
                                    element={
                                        <ProtectedCustomer>
                                            <CustomerAccount />
                                        </ProtectedCustomer>
                                    }
                                />
                                <Route
                                    path="/conta/encomendas/:id"
                                    element={
                                        <ProtectedCustomer>
                                            <CustomerOrderDetail />
                                        </ProtectedCustomer>
                                    }
                                />
                                <Route
                                    path="/conta/wishlist"
                                    element={
                                        <ProtectedCustomer>
                                            <CustomerWishlist />
                                        </ProtectedCustomer>
                                    }
                                />
                            </Route>

                            <Route path="/conta/auth-callback" element={<AuthCallback />} />
                            <Route path="/redefinir-senha" element={<ResetPassword />} />

                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route path="/admin/recuperar-senha" element={<ForgotPassword isAdmin={true} />} />
                            <Route
                                path="/admin"
                                element={
                                    <ProtectedAdmin>
                                        <AdminLayout />
                                    </ProtectedAdmin>
                                }
                            >
                                <Route index element={<AdminDashboard />} />
                                <Route path="produtos" element={<AdminProducts />} />
                                <Route path="encomendas" element={<AdminOrders />} />
                                <Route path="categorias" element={<AdminCategories />} />
                                <Route path="transporte" element={<AdminShipping />} />
                                <Route path="utilizadores" element={<AdminUsers />} />
                                <Route path="conteudos" element={<AdminContent />} />
                                <Route path="banners" element={<AdminBanners />} />
                                <Route path="fornecedores" element={<AdminSuppliers />} />
                                <Route path="campanhas" element={<AdminCampaigns />} />
                                <Route path="marcas" element={<AdminBrands />} />
                                <Route path="integracoes" element={<AdminIntegrations />} />
                                <Route path="auditoria" element={<AdminAudit />} />
                                <Route path="cupoes" element={<AdminCoupons />} />
                                <Route path="newsletter" element={<AdminNewsletter />} />
                                <Route path="2fa" element={<Admin2FA />} />
                                <Route path="analytics" element={<AdminAnalytics />} />
                                <Route path="servicos" element={<AdminServices />} />
                                <Route path="portfolio" element={<AdminPortfolio />} />
                                <Route path="pedidos-orcamento" element={<AdminContactRequests />} />
                            </Route>
                        </Routes>
                    </BrowserRouter>
                </CartProvider>
            </AuthProvider>
            </HelmetProvider>
        </div>
    );
}

export default App;
